import React, { useState, useEffect } from 'react';
import { Form2Data } from '../types';

interface Step2Props {
    onSubmit: (data: Partial<Form2Data>) => void;
    onBack: () => void;
    initialData: Partial<Form2Data>;
}

const Step2Details: React.FC<Step2Props> = ({ onSubmit, onBack, initialData }) => {
    const [formData, setFormData] = useState<Partial<Form2Data>>(initialData);
    const [errors, setErrors] = useState<Partial<Form2Data>>({});

    useEffect(() => {
        setFormData(initialData);
    }, [initialData]);

    const validate = (): boolean => {
        const newErrors: Partial<Form2Data> = {};
        if (!formData.zimmer) newErrors.zimmer = 'Bitte treffen Sie eine Auswahl.';
        if (!formData.shuttle) newErrors.shuttle = 'Bitte treffen Sie eine Auswahl.';

        if (formData.shuttle === 'ja') {
            if (!formData.ankunftZeit) newErrors.ankunftZeit = 'Bitte geben Sie eine Ankunftszeit an.';
            if (!formData.ankunftBahnhof) newErrors.ankunftBahnhof = 'Bitte wählen Sie einen Ankunftsbahnhof aus.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name as keyof Form2Data]) {
            const newErrors = { ...errors };
            delete newErrors[name as keyof Form2Data];
            setErrors(newErrors);
        }
    };

    const handleSubmit = () => {
        if (validate()) {
            onSubmit(formData);
        }
    };

    const getInputClass = (fieldName: keyof Form2Data) =>
        `mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900 ${errors[fieldName] ? 'border-red-500' : ''}`;

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-lg">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-custom mb-6 text-center">Details</h1>
            <form onSubmit={(e) => e.preventDefault()}>
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Benötigen Sie ein Zimmer für den 22. Februar 2026 zur Vorabendanreise?</label>
                        <select name="zimmer" value={formData.zimmer || ''} onChange={handleChange} className={getInputClass('zimmer')}>
                            <option value="" disabled>Bitte wählen</option>
                            <option value="ja">Ja</option>
                            <option value="nein">Nein</option>
                            <option value="weiss-nicht">Ich weiß es noch nicht</option>
                        </select>
                        {errors.zimmer && <p className="mt-1 text-sm text-red-600">{errors.zimmer}</p>}
                        {formData.zimmer === 'weiss-nicht' && (
                            <p className="mt-2 text-sm text-gray-600">Bitte informieren Sie unseren Support (support@limbach-jahresauftakt.de) bis zum 19. Januar 2026, ob Sie ein Zimmer am 22. Februar 2026 benötigen.</p>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Benötigen Sie einen Shuttle, der Sie am Bahnhof Eppstein bzw. am Fernbahnhof / Flughafen Frankfurt abholt?</label>
                        <select name="shuttle" value={formData.shuttle || ''} onChange={handleChange} className={getInputClass('shuttle')}>
                            <option value="" disabled>Bitte wählen</option>
                            <option value="ja">Ja</option>
                            <option value="nein">Nein</option>
                            <option value="weiss-nicht">Ich weiß es noch nicht</option>
                        </select>
                        {errors.shuttle && <p className="mt-1 text-sm text-red-600">{errors.shuttle}</p>}
                        {formData.shuttle === 'weiss-nicht' && (
                            <p className="mt-2 text-sm text-gray-600">Bitte informieren Sie unseren Support (support@limbach-jahresauftakt.de) bis zum 19. Januar 2026, ob Sie ein Shuttle benötigen.</p>
                        )}
                    </div>

                    {formData.shuttle === 'ja' && (
                        <>
                            <div>
                                <label htmlFor="ankunftZeit" className="block text-sm font-medium text-gray-700">Um wie viel Uhr kommt Ihre Bahn an?</label>
                                <input type="text" id="ankunftZeit" name="ankunftZeit" value={formData.ankunftZeit || ''} onChange={handleChange} className={getInputClass('ankunftZeit')} />
                                {errors.ankunftZeit && <p className="mt-1 text-sm text-red-600">{errors.ankunftZeit}</p>}
                            </div>
                            <div>
                                <label htmlFor="ankunftBahnhof" className="block text-sm font-medium text-gray-700">An welchem Bahnhof kommt ihre Bahn an?</label>
                                <select id="ankunftBahnhof" name="ankunftBahnhof" value={formData.ankunftBahnhof || ''} onChange={handleChange} className={getInputClass('ankunftBahnhof')}>
                                    <option value="" disabled>Bitte wählen</option>
                                    <option value="Bahnhof Eppstein">Bahnhof Eppstein</option>
                                    <option value="Fernbahnhof / Flughafen Frankfurt">Fernbahnhof / Flughafen Frankfurt</option>
                                </select>
                                {errors.ankunftBahnhof && <p className="mt-1 text-sm text-red-600">{errors.ankunftBahnhof}</p>}
                            </div>
                        </>
                    )}

                    <div>
                        <label htmlFor="unvertraeglichkeiten" className="block text-sm font-medium text-gray-700">Haben Sie Unverträglichkeiten, die beachtet werden müssen?</label>
                        <textarea id="unvertraeglichkeiten" name="unvertraeglichkeiten" value={formData.unvertraeglichkeiten || ''} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900"></textarea>
                    </div>

                    <div>
                        <label htmlFor="bemerkungen" className="block text-sm font-medium text-gray-700">Sonstige Bemerkungen</label>
                        <textarea id="bemerkungen" name="bemerkungen" value={formData.bemerkungen || ''} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-magenta focus:border-magenta sm:text-sm text-gray-900"></textarea>
                    </div>
                </div>

                <div className="mt-8 border-t pt-6 flex flex-col md:flex-row-reverse gap-4">
                    <button type="button" onClick={handleSubmit} className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-magenta hover:bg-berry focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-magenta disabled:opacity-50">
                        Weiter
                    </button>
                    <button type="button" onClick={onBack} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-custom disabled:opacity-50">
                        Zurück
                    </button>
                </div>
            </form>
        </div>
    );
};

export default Step2Details;
